import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { AppSettings } from '@lsq-frontend/fasttrack-shared/environment';
import { Guid } from '@lsq-frontend/remote-services/enterprise/common';
import { SendMobileUploadLinkRequest } from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { Color, Masks, Unmasks, Utilities } from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { ModalService, ModalWorkflowService } from '@lsq-frontend/common-ui';
import { filter, first, take } from 'rxjs/operators';
import {
    AdditionalUploadType,
    IBatchUploaderData,
    InvoiceFileUpload
} from '../../invoices/invoices.models';
import { InvoiceBatchService } from '../../invoices/upload-invoices/invoice-batch.service';
import { UploadInvoicesService } from '../../invoices/upload-invoices/upload-invoices.service';
import { SmsUploadService } from './../../invoices/upload-invoices/sms-upload.service';
@Component({
    selector: 'additional-uploads-modal',
    template: `
        <div class="modal-content">
            <div class="modal-header">
                <h1>Additional Uploads</h1>
            </div>
            <lsq-button appearance="transparent" class="close-button" (buttonClick)="closeModal()">
                <lsq-icon [type]="icons.Close" [color]="color.Black"> </lsq-icon>
            </lsq-button>
            <div class="upload-files-content text-center sms-upload">
                <h4>Upload your Additional Documents</h4>
                <ng-container *ngIf="!isLoaded; else uploadForm">
                    <lsq-circle-loader></lsq-circle-loader>
                </ng-container>
                <ng-template #uploadForm>
                    <lsq-button
                        [disabled]="!fileInputElement"
                        size="md"
                        (buttonClick)="fileInputElement.click()"
                    >
                        Upload Files
                    </lsq-button>
                    <div class="mobile-upload-footer">
                        <p class="send-sms-link">
                            or send an SMS link to upload an image directly from a mobile device.
                        </p>
                        <form
                            id="mobile-phone-form"
                            class="d-inline-block align-middle"
                            [formGroup]="mobileFormGroup"
                        >
                            <lsq-input
                                label="Mobile Phone Number"
                                name="phone"
                                [group]="mobileFormGroup"
                                [mask]="masks.PhoneMask"
                                type="tel"
                                [unmask]="phoneUnmask"
                                formControlName="phone"
                            >
                            </lsq-input>
                            <lsq-button
                                type="submit"
                                [group]="mobileFormGroup"
                                [disabled]="!!mobileFormGroup.invalid"
                                (buttonClick)="sendSmsMessage()"
                            >
                                Send Link
                            </lsq-button>
                        </form>
                    </div>
                </ng-template>
            </div>
        </div>
    `,
    styleUrls: ['additional-uploads-modal.component.scss']
})
export class AdditionalUploadsModalComponent implements OnInit {
    public fileInputElement: HTMLElement;
    public icons = Icons;
    public color = Color;
    public masks = Masks;
    public phoneUnmask = Unmasks.PhoneUnmask;
    public invoiceBatchKey: Guid;
    public masterInvoiceBatchKey: Guid;
    public selectedImageIndex = 0;

    // Uploader config.
    public allowedContentTypes: string;
    public parentFileUpload: InvoiceFileUpload;

    public invoiceFileUploads: InvoiceFileUpload[] = [];
    public mobileFormGroup = new FormGroup({
        phone: new FormControl('', [Validators.required, LsqValidators.phone])
    });

    public phoneNumber: string;
    public isLoaded = false;

    public additionalFileUploadsRef: IBatchUploaderData;
    constructor(
        public modalService: ModalService,
        public workflowService: ModalWorkflowService,
        public uploadInvoicesService: UploadInvoicesService,
        public invoiceBatchService: InvoiceBatchService,
        private _smsUploadService: SmsUploadService,
        private _userService: UserService,
        private _appSettings: AppSettings,
        private _alertService: AlertService
    ) {
        if (this.modalService.modal.data.phoneNumber) {
            this.phoneNumber = this.modalService.modal.data.phoneNumber;
        }

        if (!this.phoneNumber) {
            // If `phoneNumber` is empty, we want to grab the number associated with the user-state mobile field.
            this._smsUploadService.getMobileNumberState().then((number) => {
                this.phoneNumber = number;
                this.mobileFormGroup.get('phone').setValue(this.phoneNumber);
                this.isLoaded = true;
            });
        } else {
            // If `phoneNumber` is not empty, we have contacted the user-state at some point already. This condition is
            // used to avoid excess-loads from contacting the user-state endpoint every time we visit the initial page of the modal.
            this.mobileFormGroup.get('phone').setValue(this.phoneNumber);
            this.isLoaded = true;
        }
    }

    public async ngOnInit(): Promise<void> {
        let additionalFileUploadsRef: IBatchUploaderData;
        this.allowedContentTypes = this.uploadInvoicesService.allowedContentTypesForInvoices.join(
            ','
        );
        const modalData = this.modalService.modal.data;
        if (modalData) {
            this.parentFileUpload = modalData.parentFileUpload;
            this.masterInvoiceBatchKey = modalData.masterInvoiceBatchKey;

            if (this.parentFileUpload) {
                this.invoiceBatchKey = this.invoiceBatchService.invoiceBatchKey;
                additionalFileUploadsRef = await this.uploadInvoicesService.getOrCreateAdditionalUploads(
                    AdditionalUploadType.InvoiceBatchImage,
                    this.parentFileUpload.imageKey,
                    this.invoiceBatchKey,
                    () => this.triggerAlert(),
                    this._smsUploadService.getCompletedUploadsStream()
                );
            }

            if (this.masterInvoiceBatchKey) {
                additionalFileUploadsRef = await this.uploadInvoicesService.getOrCreateAdditionalUploads(
                    AdditionalUploadType.InvoiceBatch,
                    this.masterInvoiceBatchKey,
                    null,
                    () => this.triggerAlert(),
                    this._smsUploadService.getCompletedUploadsStream(this.masterInvoiceBatchKey)
                );
                this.invoiceBatchKey = Array.from(additionalFileUploadsRef.invoiceBatchKeys).pop();
            }

            if (additionalFileUploadsRef) {
                const { inputElement } = additionalFileUploadsRef;
                this.fileInputElement = inputElement;
            }

            additionalFileUploadsRef.fileUploadsStream
                .pipe(
                    filter((fileUploads) => !!fileUploads.length),
                    take(1)
                )
                .subscribe(() => {
                    this.workflowService.updateModals({
                        data: {
                            ...this.modalService.modal.data,
                            invoiceBatchKey: this.invoiceBatchKey,
                            mobileUpload: false,
                            phoneNumber: this.mobileFormGroup.value.phone
                        }
                    });

                    Utilities.executeAfterRender(() => {
                        this.workflowService.nextStep();
                    });
                });
        }
    }

    public triggerAlert(): void {
        this._alertService.error(
            `The number of files within a single submission cannot exceed ${this._appSettings.maxNumberOfFilesPerInvoiceBatch}.`
        );
    }

    public closeModal(): void {
        this.modalService.closeModal();
    }

    public async sendSmsMessage(): Promise<void> {
        this.phoneNumber = this.mobileFormGroup.value.phone;

        const user = await this._userService.user$.pipe(first()).toPromise();
        const request = new SendMobileUploadLinkRequest({
            clientName: user.currentCompany.name,
            invoiceBatchKey: this.invoiceBatchKey,
            phone: this.phoneNumber
        });
        this._smsUploadService.sendMessage(request);
        this.workflowService.updateModals({
            data: {
                ...this.modalService.modal.data,
                invoiceBatchKey: this.invoiceBatchKey,
                mobileUpload: true,
                phoneNumber: this.phoneNumber
            }
        });
        Utilities.executeAfterRender(() => {
            this.workflowService.nextStep();
        });
    }
}
